import type { NotUndefined } from 'object-hash';

import { normalizeDocument } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';
import { createHTTPError, type DebugLogger, Logger, Trace } from '@gitbook/util-debug';
import { type MaybePromise, maybePromise } from '@gitbook/util-ts';

import {
    getPageParentsIndexInRevision,
    type RevisionPageParentIndex,
    RootParentSymbol,
} from '../revisions';
import {
    asPageWithDescendants,
    type ComputedContentSourceDocument,
    type ComputedContentSourceRevision,
    type DefaultComputedContentSource,
    type Revision,
    type RevisionPageComputed,
    RevisionPageType,
    RevisionType,
} from '../types';
import { applyRevisionComputations } from './revision';
import {
    extractComputedContentSubsetState,
    fillComputedContentSource,
    getComputedSourceResultId,
} from './sources';
import type {
    ComputedContentDocumentResult,
    ComputedContentRevisionLayerResult,
    ComputedContentSourceDependency,
    ComputedContentSourceDependencyEntry,
    ComputedContentSourceResolved,
    ContentComputerState,
    ContentComputerSubsetState,
    RevisionComputations,
} from './types';
import { hash, hashDependencyRef } from './utils';

const MAX_RECURSION_DEPTH = 10;

export type RunComputeRevisionResult = {
    contentId: Revision['contentId'];
    files?: Revision['files'];
    reusableContents?: Revision['reusableContents'];
    variables?: Revision['variables'];
} & (
    | {
          pages: Revision['pages'];
          rootPages: Revision['rootPages'];
      }
    | {
          pages?: undefined;
          rootPages?: undefined;
      }
);

export type ContentComputerContext<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> = {
    /**
     * Get the version of a source type.
     * It is used to determine if the source has changed.
     */
    getSourceVersion: (
        sourceType: SourceRevision['type'] | SourceDocument['type']
    ) => Promise<string>;

    /**
     * Resolve a dependency to its latest value.
     */
    resolveDependency: (
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>['ref'],
        onUpdate: (
            entry: ComputedContentSourceDependencyEntry<
                ComputedContentSourceDependency<SourceRevision | SourceDocument>
            >
        ) => void,
        onError: (error: Error) => void
    ) => () => void;

    /**
     * Touch a dependency to indicate its use.
     * This should be used to handle any side-effects of using a dependency, like tagging the current context with the tags of the dependency.
     */
    touchDependency: (
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>['ref'],
        entry: ComputedContentSourceDependencyEntry<
            ComputedContentSourceDependency<SourceRevision | SourceDocument>
        >
    ) => void;

    /**
     * Compute the set of pages for a given source.
     */
    runComputeRevision: (input: {
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceRevision>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<RunComputeRevisionResult>;

    /**
     * Compute the document for a given source.
     */
    runComputeDocument: (input: {
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceDocument>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<Document>;

    /**
     * Wait until a promise is resolved.
     * This is used to ensure control over the async operations when executed in an environment like Cloudflare.
     */
    waitUntil?: (promise: Promise<void>) => void;
};

/**
 * The store interface represents how the content computer can persist results.
 */
export type ContentComputerStore = {
    /**
     * Get a document by its ID.
     */
    getDocument: (documentId: string) => MaybePromise<Document | undefined>;

    /**
     * Save a document.
     */
    setDocument: (documentId: string, document: Document) => MaybePromise<void>;

    /**
     * Get a revision by its ID.
     */
    getRevision: (revisionId: string) => MaybePromise<Revision | undefined>;

    /**
     * Save a revision.
     */
    setRevision: (revision: Revision) => MaybePromise<void>;
};

/**
 * Input for getting a complete computed document.
 */
export type ContentComputerCompleteDocumentInput<
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> = {
    computedSeed: string;
    computedSource: SourceDocument;
};

/**
 * Result of getting a complete computed document.
 */
export type ContentComputerCompleteDocumentResult = {
    document: Document;
    documentId: string;
};

/**
 * Options for getting a complete computed revision.
 */
export type ContentComputerGetCompleteRevisionOptions = {
    /**
     * Throw an error if the revision fails to completly compute.
     * If false, a partial revision will be returned.
     * @default false
     */
    throwOnError?: boolean;
};

/**
 * Result of getting a complete computed revision.
 */
export type ContentComputerGetCompleteRevisionResult = {
    revision: Revision;
    computations: RevisionComputations;
    error?: Error;
};

/**
 * Input for getting a complete computed revision layer.
 */
export type ContentComputerCompleteRevisionLayerInput<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
> = {
    computedSeed: string;
    computedSource: SourceRevision;
};

/**
 * Public API exposed by a content computer instance.
 */
export interface ContentComputerInstance<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> {
    context: ContentComputer<SourceRevision, SourceDocument>['context'];
    store: ContentComputer<SourceRevision, SourceDocument>['store'];
    reset: ContentComputer<SourceRevision, SourceDocument>['reset'];
    getCompleteDocument: (
        input: ContentComputerCompleteDocumentInput<SourceDocument>
    ) => MaybePromise<ContentComputerCompleteDocumentResult>;
    getCompleteDocumentAsync: (
        input: ContentComputerCompleteDocumentInput<SourceDocument>
    ) => Promise<ContentComputerCompleteDocumentResult>;
    computeDocument: ContentComputer<SourceRevision, SourceDocument>['computeDocument'];
    getCompleteRevision: (
        sourceRevision: Revision,
        options?: ContentComputerGetCompleteRevisionOptions
    ) => MaybePromise<ContentComputerGetCompleteRevisionResult>;
    getCompleteRevisionAsync: (
        sourceRevision: Revision
    ) => Promise<ContentComputerGetCompleteRevisionResult>;
    computeRevision: ContentComputer<SourceRevision, SourceDocument>['computeRevision'];
    getCompleteRevisionLayer: (
        input: ContentComputerCompleteRevisionLayerInput<SourceRevision>
    ) => MaybePromise<Revision>;
    waitPending: ContentComputer<SourceRevision, SourceDocument>['waitPending'];
}

type Subscription = {
    subscribe: (onChange: () => void, onError: (error: Error) => void) => () => void;
};

export class ContentComputer<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> implements ContentComputerInstance<SourceRevision, SourceDocument> {
    public context: ContentComputerContext<SourceRevision, SourceDocument>;
    public store: ContentComputerStore;
    private state: ContentComputerState = {
        types: {},
        dependencies: {},
    };

    private revisionLayersMemoryCache: Map<string, ComputedContentRevisionLayerResult> = new Map();
    private documentsMemoryCache: Map<string, ComputedContentDocumentResult> = new Map();

    private dependencySubscriptions: Map<string, Subscription> = new Map();

    private logger: DebugLogger;

    private pending: Array<Promise<void>> = [];

    constructor({
        context,
        store,
        logger,
    }: {
        context: ContentComputerContext<SourceRevision, SourceDocument>;
        store: ContentComputerStore;
        logger?: DebugLogger;
    }) {
        this.context = context;
        this.store = store;
        this.logger = logger ?? Logger('hive:compute');
    }

    /**
     * Reset the whole computer.
     */
    public reset() {
        this.revisionLayersMemoryCache = new Map();
        this.documentsMemoryCache = new Map();
        this.state = {
            types: {},
            dependencies: {},
        };
    }

    /**
     * Get a complete computed document.
     */
    public getCompleteDocument(
        input: ContentComputerCompleteDocumentInput<SourceDocument>
    ): MaybePromise<ContentComputerCompleteDocumentResult> {
        return maybePromise((resolve, reject) => {
            let settled = false;
            // A computation can fail while it is still being set up, before there is anything to
            // cancel; the setup cancels itself once it has something to cancel with.
            let cancelComputation: (() => void) | undefined;

            const done = () => {
                settled = true;
                cancelComputation?.();
            };

            const handleResult = (result: ComputedContentDocumentResult) => {
                if (settled) {
                    return;
                }

                if (result.complete && result.result) {
                    resolve(result.result);
                    done();
                } else if (result.error) {
                    reject(result.error);
                    done();
                }
            };

            const { result, cancel } = this.computeDocument({
                input,
                onUpdate: handleResult,
            });
            cancelComputation = cancel;

            if (settled) {
                cancel();
                return;
            }

            handleResult(result);
        });
    }

    /**
     * Get a complete computed document, always returning a promise.
     */
    public async getCompleteDocumentAsync(
        input: ContentComputerCompleteDocumentInput<SourceDocument>
    ) {
        const result = await this.getCompleteDocument(input);
        return result;
    }

    /**
     * Compute a document and be notified of its changes.
     */
    public computeDocument({
        input,
        onUpdate,
    }: {
        input: {
            computedSeed: string;
            computedSource: SourceDocument;
        };
        onUpdate: (result: ComputedContentDocumentResult) => void;
    }): {
        result: ComputedContentDocumentResult;
        cancel: () => void;
    } {
        const { computedSeed, computedSource } = input;
        let cancelled = false;

        const getResultForState = (
            state: ContentComputerSubsetState
        ): ComputedContentDocumentResult => {
            if (!state.complete) {
                return {
                    result: null,
                    complete: false,
                    error: null,
                };
            }

            const documentId = getComputedSourceResultId({
                source: computedSource,
                seed: computedSeed,
                state,
            });

            const memoryCachedDocument = this.documentsMemoryCache.get(documentId);
            if (memoryCachedDocument) {
                return memoryCachedDocument;
            }

            this.waitUntil(async () => {
                const storedDocument = await this.store.getDocument(documentId);
                if (storedDocument) {
                    const response: ComputedContentDocumentResult = {
                        result: {
                            document: storedDocument,
                            documentId,
                        },
                        complete: true,
                        error: null,
                    };
                    this.documentsMemoryCache.set(documentId, response);
                    if (!cancelled) {
                        onUpdate(response);
                    }

                    return;
                }

                let response: ComputedContentDocumentResult = {
                    result: null,
                    complete: false,
                    error: null,
                };

                try {
                    const traceLabels = { documentId, sourceType: computedSource.type };
                    const computeTrace = new Trace('hive:document:compute');
                    computeTrace.setLabels(traceLabels);
                    const rawComputedDocument = await computeTrace.wrap(async () =>
                        this.context.runComputeDocument({
                            computedSeed,
                            computedSource: fillComputedContentSource<SourceDocument>(
                                computedSource,
                                state,
                                this.context.touchDependency
                            ),
                            computationId: documentId,
                        })
                    );

                    const normalizeTrace = new Trace('hive:document:compute:normalize');
                    normalizeTrace.setLabels(traceLabels);
                    const normalizedDocument = normalizeTrace.wrapSync(() =>
                        normalizeDocument(rawComputedDocument)
                    );

                    response = {
                        result: {
                            document: normalizedDocument,
                            documentId,
                        },
                        complete: true,
                        error: null,
                    };
                } catch (error) {
                    this.logger.error(`Failed to compute document ${documentId}`, error);
                    response = {
                        result: null,
                        complete: true,
                        error: error as Error,
                    };
                }

                this.documentsMemoryCache.set(documentId, response);

                if (!cancelled) {
                    onUpdate(response);
                }

                if (response.result) {
                    try {
                        await this.store.setDocument(documentId, response.result.document);
                    } catch (error) {
                        this.logger.error(`Failed to save document ${documentId}, ignoring`, error);
                    }
                }
            });

            return {
                result: null,
                complete: false,
                error: null,
            };
        };

        const pageState = this.computeSubsetState({
            sources: [input.computedSource],
            onUpdate: (newPageState) => {
                onUpdate(getResultForState(newPageState));
            },
            onError: (error) => {
                // A source or dependency that failed will not arrive later, so the layer is done.
                // Leaving it incomplete leaves every caller waiting for good.
                onUpdate({
                    result: null,
                    complete: true,
                    error,
                });
            },
        });

        return {
            result: getResultForState(pageState.result),
            cancel: () => {
                cancelled = true;
                pageState.cancel();
            },
        };
    }

    /**
     * Get a complete computed revision.
     */
    public getCompleteRevision(
        sourceRevision: Revision,
        options: ContentComputerGetCompleteRevisionOptions = {}
    ): MaybePromise<ContentComputerGetCompleteRevisionResult> {
        const { throwOnError = false } = options;
        return maybePromise((resolve, reject) => {
            let settled = false;
            // A computation can fail while it is still being set up, before there is anything to
            // cancel; the setup cancels itself once it has something to cancel with.
            let cancelComputation: (() => void) | undefined;

            const done = () => {
                settled = true;
                cancelComputation?.();
            };

            const evaluateComputations = (chunk: RevisionComputations) => {
                if (settled || !areRevisionComputationsComplete(chunk)) {
                    return;
                }

                const applyResult = applyRevisionComputations({
                    revision: sourceRevision,
                    computations: chunk,
                });

                if (applyResult.error) {
                    if (throwOnError) {
                        reject(applyResult.error);
                    } else {
                        resolve({
                            revision: applyResult.result,
                            computations: chunk,
                            error: applyResult.error,
                        });
                    }

                    done();
                    return;
                }

                resolve({
                    revision: applyResult.result,
                    computations: chunk,
                });
                done();
            };

            const { result, cancel } = this.computeRevisionInternal({
                revision: sourceRevision,
                onUpdate: evaluateComputations,
                emitIntermediateResults: false,
            });
            cancelComputation = cancel;

            if (settled) {
                cancel();
                return;
            }

            evaluateComputations(result);
        });
    }

    /**
     * Get a complete computed revision, always returning a promise.
     */
    public async getCompleteRevisionAsync(sourceRevision: Revision) {
        const result = await this.getCompleteRevision(sourceRevision);
        return result;
    }

    /**
     * Compute a revision and subscribe to its changes.
     */
    public computeRevision({
        revision,
        onUpdate,
        recursionSafetyHashes = new Set(),
        recursionSafetyDepth = 0,
    }: {
        revision: Revision;

        /** Callback to notify the progress of the computation. */
        onUpdate: (chunk: RevisionComputations) => void;

        /** Safety check to avoid recursive computation. We store the hash of the previous source. */
        recursionSafetyHashes?: Set<string>;
        recursionSafetyDepth?: number;
    }): {
        result: RevisionComputations;
        cancel: () => void;
    } {
        return this.computeRevisionInternal({
            revision,
            onUpdate,
            recursionSafetyHashes,
            recursionSafetyDepth,
            emitIntermediateResults: true,
        });
    }

    private computeRevisionInternal({
        revision,
        onUpdate,
        recursionSafetyHashes = new Set(),
        recursionSafetyDepth = 0,
        emitIntermediateResults,
    }: {
        revision: Revision;
        onUpdate: (chunk: RevisionComputations) => void;
        recursionSafetyHashes?: Set<string>;
        recursionSafetyDepth?: number;
        emitIntermediateResults: boolean;
    }): {
        result: RevisionComputations;
        cancel: () => void;
    } {
        const cancelCallbacks: (() => void)[] = [];

        // Locate all computed pages in the source revision
        const toCompute = Object.values(revision.pages).filter(
            (page) => page.type === RevisionPageType.Computed
        );

        let parentsIndex: RevisionPageParentIndex | undefined;
        let result: RevisionComputations = {
            pages: Object.fromEntries(
                toCompute.map((computedPage) => {
                    parentsIndex = parentsIndex || getPageParentsIndexInRevision(revision);

                    const parentId = parentsIndex.get(computedPage.id);
                    if (!parentId) {
                        throw new Error(`Couldn't find parent of page ${computedPage.id}`);
                    }

                    const pages =
                        parentId === RootParentSymbol
                            ? revision.rootPages
                            : asPageWithDescendants(revision.pages[parentId]).pages;
                    const index = pages.indexOf(computedPage.id);

                    return [
                        computedPage.id,
                        {
                            parentId,
                            computedPage,
                            index,
                            result: null,
                            complete: false,
                            error: null,
                        },
                    ];
                })
            ),
        };

        const updateResultForPage = (pageId: string, layer: ComputedContentRevisionLayerResult) => {
            if (!emitIntermediateResults) {
                result.pages[pageId] = {
                    ...result.pages[pageId],
                    ...layer,
                };

                return result;
            }

            result = {
                ...result,
                pages: {
                    ...result.pages,
                    [pageId]: {
                        ...result.pages[pageId],
                        ...layer,
                    },
                },
            };

            return result;
        };

        for (const page of toCompute) {
            const pageHash = hash(page.computed);

            if (recursionSafetyDepth > MAX_RECURSION_DEPTH) {
                updateResultForPage(page.id, {
                    result: null,
                    complete: true,
                    error: new createHTTPError.LoopDetected(
                        `Max recursion depth reached at depth ${recursionSafetyDepth} with source ${JSON.stringify(page.computed)}`
                    ),
                });
            } else if (recursionSafetyHashes.has(pageHash)) {
                updateResultForPage(page.id, {
                    result: null,
                    complete: true,
                    error: new createHTTPError.LoopDetected(
                        `Infinite recursion detected at depth ${recursionSafetyDepth} with source ${JSON.stringify(page.computed)}`
                    ),
                });
            } else {
                const pageComputation = this.computeRevisionLayer({
                    computedSeed: getPageComputedSeed(page),
                    computedSource: page.computed as SourceRevision,
                    onUpdate: (layer) => {
                        const updatedResult = updateResultForPage(page.id, layer);
                        if (
                            emitIntermediateResults ||
                            areRevisionComputationsComplete(updatedResult)
                        ) {
                            onUpdate(updatedResult);
                        }
                    },
                    recursionSafetyHashes: new Set([...recursionSafetyHashes, pageHash]),
                    recursionSafetyDepth: recursionSafetyDepth + 1,
                    emitIntermediateResults,
                });

                if (pageComputation.result) {
                    updateResultForPage(page.id, pageComputation.result);
                }

                cancelCallbacks.push(pageComputation.cancel);
            }
        }

        return {
            result,
            cancel: () => {
                cancelCallbacks.forEach((cancel) => cancel());
            },
        };
    }

    /**
     * Compute a complete revision layer.
     */
    public getCompleteRevisionLayer(
        input: ContentComputerCompleteRevisionLayerInput<SourceRevision>
    ): MaybePromise<Revision> {
        return maybePromise((resolve, reject) => {
            let settled = false;
            // A computation can fail while it is still being set up, before there is anything to
            // cancel; the setup cancels itself once it has something to cancel with.
            let cancelComputation: (() => void) | undefined;

            const done = () => {
                settled = true;
                cancelComputation?.();
            };

            const handleResult = (result: ComputedContentRevisionLayerResult) => {
                if (settled || !result.complete) {
                    return;
                }

                if (result.error) {
                    reject(result.error);
                    done();
                } else if (result.result) {
                    resolve(result.result);
                    done();
                } else {
                    throw new Error('Invalid state');
                }
            };

            const { result, cancel } = this.computeRevisionLayer({
                computedSeed: input.computedSeed,
                computedSource: input.computedSource,
                onUpdate: handleResult,
                emitIntermediateResults: false,
            });
            cancelComputation = cancel;

            if (settled) {
                cancel();
                return;
            }

            handleResult(result);
        });
    }

    /**
     * Compute a specific revision layer, corresponding to a specific computed page.
     */
    private computeRevisionLayer({
        computedSeed,
        computedSource,
        onUpdate,
        recursionSafetyHashes = new Set(),
        recursionSafetyDepth = 0,
        emitIntermediateResults,
    }: {
        computedSeed: string;
        computedSource: SourceRevision;

        /** Callback to notify with the revision computed */
        onUpdate: (state: ComputedContentRevisionLayerResult) => void;

        /** Safety check to avoid recursive computation. We store the hash of the previous source. */
        recursionSafetyHashes?: Set<string>;
        recursionSafetyDepth?: number;
        emitIntermediateResults: boolean;
    }): {
        result: ComputedContentRevisionLayerResult;
        cancel: () => void;
    } {
        let cancelled = false;
        let cancelRecursiveStream: (() => void) | undefined;

        const transformRevisionLayerComputations = (
            revisionLayer: Revision,
            computations: RevisionComputations
        ): ComputedContentRevisionLayerResult => {
            if (!emitIntermediateResults && !areRevisionComputationsComplete(computations)) {
                return {
                    result: null,
                    complete: false,
                    error: null,
                };
            }

            const applyResult = applyRevisionComputations({
                revision: revisionLayer,
                computations,
            });

            if (applyResult.error) {
                return {
                    result: null,
                    complete: true,
                    error: applyResult.error,
                };
            }

            return {
                result: applyResult.result,
                complete: applyResult.complete,
                error: null,
            };
        };

        const transformRevisionLayer = (layer: ComputedContentRevisionLayerResult) => {
            const { result } = layer;
            if (!result) {
                // If the layer is not computed yet, return the layer as is
                return layer;
            }

            // Compute recursively the layer and stream the updates
            const recursiveStream = this.computeRevisionInternal({
                revision: result,
                onUpdate: (computations) => {
                    onUpdate(transformRevisionLayerComputations(result, computations));
                },
                recursionSafetyHashes,
                recursionSafetyDepth,
                emitIntermediateResults,
            });

            cancelRecursiveStream = recursiveStream.cancel;
            return transformRevisionLayerComputations(result, recursiveStream.result);
        };

        const getLayerForState = (state: ContentComputerSubsetState) => {
            if (cancelRecursiveStream) {
                cancelRecursiveStream();
                cancelRecursiveStream = undefined;
            }

            if (!state.complete) {
                return transformRevisionLayer({
                    result: null,
                    complete: false,
                    error: null,
                });
            }

            const layerRevisionId = getComputedSourceResultId({
                source: computedSource,
                seed: computedSeed,
                state,
            });

            // Try from the memory cache
            const cachedRevisionLayer = this.revisionLayersMemoryCache.get(layerRevisionId);
            if (cachedRevisionLayer) {
                return transformRevisionLayer(cachedRevisionLayer);
            }

            this.waitUntil(async () => {
                // Try from the store
                const storedRevision = await this.store.getRevision(layerRevisionId);
                if (storedRevision) {
                    this.logger.info(`Found revision ${layerRevisionId} in store`);

                    const storedRevisionLayer: ComputedContentRevisionLayerResult = {
                        result: storedRevision,
                        complete: true,
                        error: null,
                    };
                    this.revisionLayersMemoryCache.set(layerRevisionId, storedRevisionLayer);

                    if (!cancelled) {
                        onUpdate(transformRevisionLayer(storedRevisionLayer));
                    }

                    return;
                }

                // Compute the layer revision
                this.logger.info(`Computing revision ${layerRevisionId}`);

                let computedRevisionLayer: ComputedContentRevisionLayerResult = {
                    result: null,
                    complete: false,
                    error: null,
                };
                try {
                    const result = await this.context.runComputeRevision({
                        computedSeed,
                        computedSource: fillComputedContentSource<SourceRevision>(
                            computedSource,
                            state,
                            this.context.touchDependency
                        ),
                        computationId: layerRevisionId,
                    });

                    computedRevisionLayer = {
                        result: {
                            id: layerRevisionId,
                            type: RevisionType.Computed,
                            edits: {},
                            parents: [],
                            contentId: result.contentId,
                            variables: result.variables ?? {},
                            reusableContents: result.reusableContents ?? {},
                            files: result.files ?? {},
                            pages: result.pages ?? {},
                            rootPages: result.rootPages ?? [],
                        },
                        complete: true,
                        error: null,
                    };
                } catch (error) {
                    this.logger.error(`Failed to compute revision ${layerRevisionId}`, error);
                    computedRevisionLayer = {
                        result: null,
                        complete: true,
                        error: error as Error,
                    };
                }

                this.revisionLayersMemoryCache.set(layerRevisionId, computedRevisionLayer);

                if (!cancelled) {
                    onUpdate(transformRevisionLayer(computedRevisionLayer));
                }

                // Save the revision in the store
                if (computedRevisionLayer.result) {
                    try {
                        await this.store.setRevision(computedRevisionLayer.result);
                    } catch (error) {
                        this.logger.error(
                            `Failed to save revision ${layerRevisionId}, ignoring`,
                            error
                        );
                    }
                }
            });

            return transformRevisionLayer({
                result: null,
                complete: false,
                error: null,
            });
        };

        const pageState = this.computeSubsetState({
            sources: [computedSource],
            onUpdate: (newPageState) => {
                onUpdate(getLayerForState(newPageState));
            },
            onError: (error) => {
                // A source or dependency that failed will not arrive later, so the layer is done.
                // Leaving it incomplete leaves every caller waiting for good.
                onUpdate({
                    result: null,
                    complete: true,
                    error,
                });
            },
        });

        return {
            result: getLayerForState(pageState.result),
            cancel: () => {
                cancelled = true;
                pageState.cancel();
                cancelRecursiveStream?.();
            },
        };
    }

    /**
     * Compute a subset of the state and stream updates as dependencies change.
     */
    private computeSubsetState({
        sources,
        onUpdate,
        onError,
    }: {
        /** Sources to compute. */
        sources: Array<SourceRevision | SourceDocument>;

        /** Callback to notify when the state is updated. */
        onUpdate: (state: ContentComputerSubsetState) => void;
        onError: (error: Error) => void;
    }): {
        result: ContentComputerSubsetState;
        cancel: () => void;
    } {
        const cancelCallbacks: (() => void)[] = [];

        const initial = extractComputedContentSubsetState(this.state, sources);

        const onStateChange = () => {
            const { result } = extractComputedContentSubsetState(this.state, sources);
            onUpdate(result);
        };

        for (const dependency of initial.allDependencies) {
            cancelCallbacks.push(
                this.subscribeToDependency({
                    dependency,
                    onUpdate: onStateChange,
                    onError,
                })
            );
        }

        for (const sourceType of initial.sourceTypesToFetch) {
            const { cancel } = this.fetchSourceType({
                sourceType,
                onUpdate: onStateChange,
                onError,
            });

            cancelCallbacks.push(cancel);
        }

        return {
            // Dependency resolvers can synchronously seed this.state while being subscribed.
            result: extractComputedContentSubsetState(this.state, sources).result,
            cancel: () => {
                cancelCallbacks.forEach((cancel) => cancel());
            },
        };
    }

    /**
     * Subscribe to changes of a dependency.
     */
    private subscribeToDependency({
        dependency,
        onUpdate,
        onError,
    }: {
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>;
        onUpdate: () => void;
        onError: (error: Error) => void;
    }): () => void {
        let subscription: Subscription | undefined;

        try {
            subscription = this.getDependencySubscription(dependency);
            return subscription.subscribe(onUpdate, onError);
        } catch (error) {
            onError(error as Error);
            return () => {};
        }
    }

    /**
     * Fetch and start listening to changes of a dependency.
     */
    private getDependencySubscription(
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>
    ): Subscription {
        const dependencyHash = hashDependencyRef(dependency);
        const existingSubscription = this.dependencySubscriptions.get(dependencyHash);

        if (existingSubscription) {
            return existingSubscription;
        }

        let state: 'resolved' | 'cancelled' | 'pending' = 'pending';
        // The subscription outlives the callbacks the error was delivered to, so the error is kept
        // to be replayed: a computation subscribing afterwards would otherwise wait for a value the
        // dependency has already failed to produce.
        let lastError: Error | undefined;

        const changeCallbacks = new Set<() => void>();
        const errorCallbacks = new Set<(error: Error) => void>();

        const onValue = (
            entry: ComputedContentSourceDependencyEntry<
                ComputedContentSourceDependency<SourceRevision | SourceDocument>
            >
        ) => {
            this.state.dependencies[dependencyHash] = {
                versionHash: hash(entry.value as NotUndefined),
                ref: dependency.ref,
                value: entry.value,
                tags: entry.tags,
            };

            lastError = undefined;

            if (state !== 'cancelled') {
                changeCallbacks.forEach((callback) => callback());
                state = 'resolved';
            }
        };

        const onError = (error: Error) => {
            lastError = error;
            errorCallbacks.forEach((callback) => callback(error));
        };

        // Resolved before anything that could cancel it exists, so a cancellation arriving while
        // the dependency reports back synchronously always has a canceller to call.
        const cancelResolve = this.context.resolveDependency(dependency.ref, onValue, onError);

        const cancel = () => {
            state = 'cancelled';
            this.dependencySubscriptions.delete(dependencyHash);
            cancelResolve();
            // Keep the last resolved value so one-shot computations can synchronously reuse
            // cached results when a caller retries after the promise resolves.
        };

        const subscription: Subscription = {
            subscribe: (changeCallback, errorCallback) => {
                if (state === 'resolved') {
                    Promise.resolve().then(changeCallback);
                } else if (lastError) {
                    const error = lastError;
                    Promise.resolve().then(() => errorCallback(error));
                }
                changeCallbacks.add(changeCallback);
                errorCallbacks.add(errorCallback);
                return () => {
                    changeCallbacks.delete(changeCallback);
                    errorCallbacks.delete(errorCallback);
                    if (changeCallbacks.size === 0) {
                        cancel();
                    }
                };
            },
        };
        this.dependencySubscriptions.set(dependencyHash, subscription);

        return subscription;
    }

    /**
     * Fetch a source type.
     */
    private fetchSourceType({
        sourceType,
        onUpdate,
        onError,
    }: {
        sourceType: SourceDocument['type'] | SourceRevision['type'];
        onUpdate: () => void;
        onError: (error: Error) => void;
    }): {
        cancel: () => void;
    } {
        let cancelled = false;

        this.waitUntil(async () => {
            try {
                const version = await this.context.getSourceVersion(sourceType);
                this.state.types[sourceType] = version;

                if (!cancelled) {
                    onUpdate();
                }
            } catch (error) {
                this.logger.error(`Failed to fetch source type ${sourceType}`, error);

                if (!cancelled) {
                    onError(error as Error);
                }
            }
        });

        return {
            cancel: () => {
                cancelled = true;
            },
        };
    }

    /**
     * Wait for an async operation to be resolved.
     */
    private waitUntil(fn: () => Promise<void>) {
        const promise = fn().catch((error) => {
            this.logger.error('Error while waiting for async operation', error);
            throw error;
        });
        if (this.context.waitUntil) {
            this.context.waitUntil(promise);
        }
        this.pending.push(promise);
        promise.finally(() => {
            this.pending.splice(this.pending.indexOf(promise), 1);
        });
    }

    /**
     * Wait for all pending operations to be resolved.
     */
    public async waitPending() {
        await Promise.all(this.pending);
    }
}

/**
 * Get the computed seed for a computed page.
 */
export function getPageComputedSeed(page: RevisionPageComputed): string {
    return page.id;
}

function areRevisionComputationsComplete(computations: RevisionComputations): boolean {
    return Object.values(computations.pages).every((entry) => entry.complete);
}
